import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  CanvasApi,
  extractCanvasData,
  extractCanvasListData,
} from './canvasApi';
import {
  Canvas,
  CreateCanvasRequest,
  UpdateCanvasRequest,
} from '../../types/canvas.types';

// Query keys
export const canvasKeys = {
  all: ['canvases'] as const,
  lists: () => [...canvasKeys.all, 'list'] as const,
  list: (accountId: string) => [...canvasKeys.lists(), accountId] as const,
  details: () => [...canvasKeys.all, 'detail'] as const,
  detail: (accountId: string, canvasId: string) =>
    [...canvasKeys.details(), accountId, canvasId] as const,
  bySlug: (accountId: string, canvasSlug: string) =>
    [...canvasKeys.details(), accountId, 'slug', canvasSlug] as const,
};

// Hook to fetch all canvases for an account
export function useCanvases(accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery({
    queryKey: canvasKeys.list(finalAccountId!),
    queryFn: async () => {
      const response = await CanvasApi.listCanvases(finalAccountId!).run();
      return extractCanvasListData(response);
    },
    enabled: !!finalAccountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a single canvas
export function useCanvas(canvasId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery({
    queryKey: canvasKeys.detail(finalAccountId!, canvasId),
    queryFn: async () => {
      const response = await CanvasApi.getCanvas(
        finalAccountId!,
        canvasId,
      ).run();
      return extractCanvasData(response);
    },
    enabled: !!finalAccountId && !!canvasId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a canvas by slug
export function useCanvasBySlug(accountId: string, canvasSlug: string) {
  return useQuery({
    queryKey: canvasKeys.bySlug(accountId, canvasSlug),
    queryFn: async () => {
      console.log('useCanvasBySlug: Fetching canvases for', {
        accountId,
        canvasSlug,
      });

      // Fetch all canvases and find the one with matching slug
      const response = await CanvasApi.listCanvases(accountId).run();
      const canvases = extractCanvasListData(response);

      console.log(
        'useCanvasBySlug: Found canvases:',
        canvases.map((c) => ({ id: c.id, name: c.name, slug: c.slug })),
      );

      const canvas = canvases.find((c) => c.slug === canvasSlug);

      console.log(
        'useCanvasBySlug: Looking for slug:',
        canvasSlug,
        'Found canvas:',
        canvas,
      );

      if (!canvas) {
        throw new Error(`Canvas with slug "${canvasSlug}" not found`);
      }

      return canvas;
    },
    enabled: !!accountId && !!canvasSlug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to create a new canvas
export function useCreateCanvas(accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCanvasRequest) => {
      const response = await CanvasApi.createCanvas(
        finalAccountId!,
        data,
      ).run();
      return extractCanvasData(response);
    },
    onSuccess: (newCanvas) => {
      // Invalidate and refetch canvases list
      queryClient.invalidateQueries({
        queryKey: canvasKeys.list(finalAccountId!),
      });

      // Optimistically update the cache
      queryClient.setQueryData(
        canvasKeys.detail(finalAccountId!, newCanvas.id),
        newCanvas,
      );
    },
  });
}

// Hook to update a canvas
export function useUpdateCanvas(canvasId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCanvasRequest) => {
      const response = await CanvasApi.updateCanvas(
        finalAccountId!,
        canvasId,
        data,
      ).run();
      return extractCanvasData(response);
    },
    onSuccess: (updatedCanvas) => {
      // Update the specific canvas in cache
      queryClient.setQueryData(
        canvasKeys.detail(finalAccountId!, canvasId),
        updatedCanvas,
      );

      // Update the canvas in the list
      queryClient.setQueryData(
        canvasKeys.list(finalAccountId!),
        (oldData: Canvas[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((canvas) =>
            canvas.id === canvasId ? updatedCanvas : canvas,
          );
        },
      );
    },
  });
}

// Hook to delete a canvas
export function useDeleteCanvas(accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (canvasId: string) => {
      await CanvasApi.deleteCanvas(finalAccountId!, canvasId).run();
      return canvasId;
    },
    onSuccess: (deletedCanvasId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: canvasKeys.detail(finalAccountId!, deletedCanvasId),
      });

      // Update the list
      queryClient.setQueryData(
        canvasKeys.list(finalAccountId!),
        (oldData: Canvas[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((canvas) => canvas.id !== deletedCanvasId);
        },
      );
    },
  });
}
